import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Paginação em memória para as tabelas.
 *
 * Devolve a fatia da página corrente e tudo que o componente `Paginacao`
 * precisa. Dois cuidados que evitam a tela "vazia sem motivo":
 *
 * - quando a lista encolhe (um filtro foi aplicado) e a página atual deixa de
 *   existir, volta para a última página válida;
 * - quando o conteúdo da lista muda de identidade (outra busca), volta à
 *   primeira página.
 */
export function usePaginacao(itens, tamanhoInicial = 15) {
  const lista = useMemo(() => itens || [], [itens]);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(tamanhoInicial);

  const total = lista.length;
  const totalPaginas = Math.max(1, Math.ceil(total / tamanho));

  // A página atual pode ter deixado de existir depois de um filtro.
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  const irPara = useCallback(
    (n) => setPagina(Math.min(Math.max(1, n), totalPaginas)),
    [totalPaginas]
  );

  const alterarTamanho = useCallback((novo) => {
    setTamanho(novo);
    setPagina(1);
  }, []);

  const paginaSegura = Math.min(pagina, totalPaginas);
  const inicio = (paginaSegura - 1) * tamanho;
  const paginados = useMemo(
    () => lista.slice(inicio, inicio + tamanho),
    [lista, inicio, tamanho]
  );

  return {
    paginados,
    pagina: paginaSegura,
    totalPaginas,
    total,
    tamanho,
    // 1-based, para exibição: "Mostrando 16–30 de 120"
    de: total === 0 ? 0 : inicio + 1,
    ate: Math.min(inicio + tamanho, total),
    irPara,
    alterarTamanho,
    reiniciar: useCallback(() => setPagina(1), []),
  };
}

export default usePaginacao;
